/**
 * The session drawer's "waiting for you" card and its Model fact. The card shows what the
 * session asked, whole (ADR 0056): the message as Markdown scrolled to its end, the closing
 * question emphasised, quick replies that fill the message box, and where to answer it. A turn
 * that ended on a question with nothing held reads "asked" (neutral); a held request reads
 * "needs your decision" (attention). When Orgus cannot show the ask, the card says why and
 * where to look. A question, plan or MCP form held by a hook has its own card, answerable in
 * place (AskCard in SessionApprovals, R-HL-6), so this card steps aside for it.
 */
import type { Session } from '@orgus/protocol';
import { Hand, MessageCircleQuestion, Radar, SquareTerminal } from '@orgus/ui/icons';
import { useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Markdown, RelativeTime, Tooltip, cn } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useLogStream } from '../../lib/useLogStream.ts';
import { useApprovals } from '../approvals/live.ts';
import { isAnswerable } from '../approvals/askModel.ts';
import { speakerOf } from '../adapters/state.ts';
import { modelLabel } from '../sessions/labels.ts';
import { askViewOf } from './asks.ts';
import type { AskView } from './asks.ts';
import { answerRoute, answerText, launchModelOf, sessionModelOf, withoutOptionList } from './model.ts';
import { fillReply } from './replyDraft.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { AskActions, HandBackButton, SetAsideLine } from '../asks/AskActions.tsx';

/** What the session asked, with its pending approvals taken into account (ADR 0056 levels). */
export function useAskView(s: Session): AskView & { approvalPending: boolean } {
  const { approvals } = useApprovals();
  const approvalPending = (approvals?.pending ?? []).some((a) => a.state === 'pending' && a.sessionId === s.id);
  return { ...askViewOf(s, approvalPending), approvalPending };
}

/** The message above the question: scrolled to its end, so the newest words show first. */
function AskBody({ source }: { source: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [source]);
  return (
    <div ref={ref} tabIndex={0} aria-label="Its message" className="max-h-64 overflow-y-auto rounded-control focus-visible:outline-2 focus-visible:outline-accent">
      <Markdown source={source} size="sm" className="text-fg-muted" />
    </div>
  );
}

function missingText(missing: NonNullable<AskView['missing']>, machine: string): string {
  return missing === 'content-off'
    ? `Orgus cannot show what it asked: this workspace keeps transcript text on ${machine} (Settings, Data sent). Read it in the session's terminal there.`
    : `Orgus has not received its last message. Read what it needs in the session's terminal on ${machine}.`;
}

export function WaitingCallout({
  session: s,
  machineName,
  composer,
  controlled,
  liveTerminal,
  onOpenTerminal,
}: {
  session: Session;
  machineName: string;
  /** A headless session Orgus launched takes messages below the timeline. */
  composer: boolean;
  /** Session control is on for its machine: the message box reaches it. */
  controlled: boolean;
  /** It runs in a terminal Orgus started (the Terminal tab). */
  liveTerminal: boolean;
  onOpenTerminal: () => void;
}) {
  const [, setParams] = useSearchParams();
  const v = useAskView(s);
  // Dismiss and snooze (ADR 0056): an asked question can be set aside; a held request of a
  // controlled session can be handed back to its own dialog.
  const ny = useNeedsYouState();
  const { approvals } = useApprovals();
  const askItem = [...ny.asked, ...ny.open].find((i) => i.sessionId === s.id) ?? null;
  const setAside = ny.setAside.find((x) => x.item.sessionId === s.id)?.record ?? null;
  const handBack = (approvals?.pending ?? []).find((a) => a.state === 'pending' && a.sessionId === s.id && a.fallback === 'session') ?? null;
  const route = answerRoute({ approvalPending: v.approvalPending, liveTerminal, composer, controlled });
  const speaker = speakerOf(s);
  // The ask card below shows the whole question with its controls: one place to answer.
  if ((approvals?.pending ?? []).some((a) => a.state === 'pending' && a.sessionId === s.id && isAnswerable(a))) return null;
  // Nothing held and nothing to show: say it waits, not that it asks (ADR 0056).
  const unknown = !!v.missing && !v.approvalPending;
  const calm = v.level === 'asked' || unknown;
  const heading = unknown ? `${speaker} is waiting` : v.level === 'asked' ? `${speaker} asked you` : `${speaker} needs your decision`;
  const canReply = route === 'message' || route === 'composer';
  const Icon = calm ? MessageCircleQuestion : Hand;
  const quick = canReply && v.options.length > 0;
  // The quick replies name the choices: the message's own list of them would say it twice.
  const body = v.body && quick ? withoutOptionList(v.body, v.options) : v.body;
  // Where to answer goes without saying when its control is right here (message box, request card, terminal button).
  const where = route === 'watched' ? answerText(route, machineName) : null;
  return (
    <section
      role="status"
      aria-label={heading}
      className={cn('flex gap-3 rounded-card border px-4 py-3', calm ? 'border-border bg-surface' : 'border-status-attention-line bg-status-attention-soft')}
    >
      <span className="flex h-5 shrink-0 items-center">
        <Icon className={cn('size-4', calm ? 'text-fg-muted' : 'text-status-attention')} aria-hidden />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="flex items-baseline gap-2">
          <span className={cn('text-ui font-medium', calm ? 'text-fg' : 'text-status-attention-ink')}>{heading}</span>
          {v.askedAt && <RelativeTime ts={v.askedAt} className="text-caption text-fg-subtle" />}
        </p>
        {body && <AskBody source={body} />}
        {v.question && <Markdown source={v.question} className="font-medium text-fg" />}
        {v.missing && <p className="text-body text-fg-muted">{missingText(v.missing, machineName)}</p>}
        {!v.body && !v.question && !v.missing && <p className="text-body text-fg">It needs an answer or a permission before it can go on.</p>}
        {quick && (
          <div role="group" aria-label="Quick replies" className="flex flex-wrap gap-2">
            {v.options.map((o) => (
              <Button key={o} size="sm" variant="secondary" className="max-w-full" onClick={() => fillReply(s.id, o)}>
                <span className="truncate">{o}</span>
              </Button>
            ))}
          </div>
        )}
        {where && <p className="text-caption text-fg-muted">{where}</p>}
        {setAside ? <SetAsideLine record={setAside} /> : askItem && <AskActions ask={askItem} align="start" />}
        {handBack && <HandBackButton approvalId={handBack.id} />}
        {route === 'terminal' && (
          <div>
            <Button size="sm" variant="secondary" icon={<SquareTerminal />} onClick={onOpenTerminal}>
              Answer in its terminal
            </Button>
          </div>
        )}
        {route === 'watched' && (
          <div>
            <Button size="sm" variant="secondary" icon={<Radar />} onClick={() =>
                // The switch is in this drawer, on Details (Autonomy).
                setParams(
                  (p) => {
                    const n = new URLSearchParams(p);
                    n.set('tab', 'details');
                    return n;
                  },
                  { replace: true, preventScrollReset: true },
                )
              }>
              Turn on session control
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * The model a session runs, in words. Before its first reply a session reports none: then
 * the model it was launched with (from its log, for one Orgus started), else "Not reported yet".
 */
export function SessionModelText({ session: s }: { session: Session }) {
  const known = sessionModelOf(s);
  const live = useOrgus((st) => st.mode) === 'live';
  const ask = !known && s.origin === 'orgus';
  const { log } = useLogStream(ask ? { type: 'session', id: s.id } : null, live && ask);
  const launched = ask && log?.text ? launchModelOf(log.text) : null;
  if (known) return <>{modelLabel(known)}</>;
  if (launched) {
    return (
      <Tooltip content={`Started with ${launched}. It has not replied yet, so it has not confirmed the model.`}>
        <span tabIndex={0}>{modelLabel(launched)}</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip content="A session names its model in its first reply. This one has not replied yet; it runs the default model of its machine.">
      <span tabIndex={0} className="text-fg-muted">
        Not reported yet
      </span>
    </Tooltip>
  );
}
